import axios from 'axios';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';

import { useIsSegmentLoaded } from 'helpers';

// Timeout for LD to load in milliseconds
const LD_TIMEOUT = 5000;

export const getAnonymousUserHash = async (anonymousId) => {
  try {
    const res = await axios.get(`${process.env.GATSBY_REACT_APP_API}users/users/${anonymousId}/flags/hash`);
    return res.data.secure_mode_hash;
  } catch (err) {
    console.error(err);
  }
};

// Identifies an anonymous users and returns if ld is loaded
export const useIsLDLoaded = () => {
  const ldClient = useLDClient();
  const [isLdLoaded, setIsLdLoaded] = useState<boolean>(false);
  const [ldAnonymousId, setLdAnonymousId] = useState<string>();
  const isSegmentLoaded = useIsSegmentLoaded();

  // Sets LD anonymousId to Segment anonymousId so we can map user activity in the LD experiment to Segment events
  if (isSegmentLoaded && !ldAnonymousId) {
    try {
      const { anonymousId } = window.analytics.user();
      const id = anonymousId();
      setLdAnonymousId(id);
    } catch (err) {
      console.error('Error getting anonymousId from Segment:', err);
    }
  }

  useEffect(() => {
    // Set a timeout to ensure the page loads even if LD or Segment fails
    // This is particularly important when users have ad blockers that may block Segment,
    // which would otherwise prevent pages from loading that depend on LaunchDarkly
    const timeoutId = setTimeout(() => {
      if (!isLdLoaded) {
        console.warn('LaunchDarkly initialization timed out. Loading page with default values.');
        setIsLdLoaded(true);
      }
    }, LD_TIMEOUT);

    return () => clearTimeout(timeoutId);
  }, []);

  // Identifies LaunchDarkly anonymous user + sets load state
  useEffect(() => {
    const identifyLdUser = async () => {
      try {
        const hash = await getAnonymousUserHash(ldAnonymousId);
        await ldClient?.identify({ key: ldAnonymousId, anonymous: true }, hash, () => setIsLdLoaded(true));
      } catch (err) {
        console.error(err);
        setIsLdLoaded(true);
      }
    };

    if (ldClient && ldAnonymousId) identifyLdUser();

    // Segment doesn't load in development, so we set isLdLoaded immediately
    if (process.env.NODE_ENV === 'development') setIsLdLoaded(true);
  }, [ldClient, ldAnonymousId]);

  return isLdLoaded;
};
